import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { BACKOFFICE_QUERIES } from "./queries";
import { bffInstance } from "@shared/services";
import { handleApiError } from "@/modules/shared/helpers/api-error.helper";

import type {
  ITerminalListFilters,
  ITerminalListResponse,
  ICreateTerminalPayload,
  ICreateTerminalResponse,
  ITerminalDetails,
  IResolveNamesPayload,
  IResolveNamesResponse,
  IConfigureTerminalPayload,
  IConfigureTerminalResponse,
  IUnconfigureTerminalPayload,
  ICsvBatchPayload,
} from "../types/terminal";

const URLS = {
  terminals: "/barte/v1/pos/terminals",
  terminalDetails: (serialNumber: string) => `/barte/v1/pos/terminals/${serialNumber}`,
  terminalsConfigure: "/barte/v1/pos/terminals/configure",
  terminalsUnconfigure: "/barte/v1/pos/terminals/unconfigure",
  terminalsCsv: (processingType: string) => `/barte/v1/pos/terminals/csv?processingType=${processingType}`,
  terminalsCreate: "/backoffice/v1/terminals",
  terminalsRegisterBatch: "/barte/v2/pos",
  resolveNames: "/barte/v1/pos/resolve-names",
  configureSupport: "/barte/v1/pos/configure-support",
} as const;

export const POS_CHECKLIST_URL = "https://pos-checklist-testes.stefano-costa.workers.dev";

const api = bffInstance({
  enableXTokenApi: false,
  enableBearerToken: true,
});

export const useGetTerminalsList = (
  {
    page,
    limit,
    status,
    serialNumbers,
    barteCompanyIds,
    companyName,
    barteSellerIds,
    sellerName,
  }: ITerminalListFilters,
  queryOptions?: { staleTime?: number },
) => {
  const { t } = useTranslation();

  const getTerminalsList = async (): Promise<{
    success: boolean;
    message: string;
    data?: ITerminalListResponse;
  }> => {
    try {
      const params: Record<string, unknown> = {
        page,
        limit,
        ...(status?.length && { status }),
        ...(serialNumbers?.length && { serialNumbers }),
        ...(barteCompanyIds?.length && { barteCompanyIds }),
        ...(companyName && { companyName }),
        ...(barteSellerIds?.length && { barteSellerIds }),
        ...(sellerName && { sellerName }),
      };

      const { data } = await api.get(URLS.terminals, {
        params,
        paramsSerializer: {
          indexes: null,
        },
      });

      return {
        success: true,
        message: t("backoffice.terminalsServiceListSuccess"),
        data,
      };
    } catch (error: any) {
      return handleApiError(error, t("backoffice.terminalsServiceListError"));
    }
  };

  return useQuery({
    queryKey: [
      BACKOFFICE_QUERIES.TERMINALS,
      page,
      limit,
      status,
      serialNumbers,
      barteCompanyIds,
      companyName,
      barteSellerIds,
      sellerName,
    ],
    queryFn: () => getTerminalsList(),
    staleTime: queryOptions?.staleTime,
  });
};

export const useCreateTerminal = () => {
  const { t } = useTranslation();

  const createTerminal = async (
    payload: ICreateTerminalPayload,
  ): Promise<{
    success: boolean;
    message: string;
    data?: ICreateTerminalResponse;
  }> => {
    try {
      const { data } = await api.post(URLS.terminalsCreate, payload);

      return {
        success: true,
        message: t("backoffice.terminalsServiceCreateSuccess"),
        data,
      };
    } catch (error: any) {
      return handleApiError(error, t("backoffice.terminalsServiceCreateError"));
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.TERMINALS, "create"],
    mutationFn: (payload: ICreateTerminalPayload) => createTerminal(payload),
  });
};

export const useRegisterTerminalsBatch = () => {
  const { t } = useTranslation();

  const registerTerminalsBatch = async (
    payload: ICreateTerminalPayload,
  ): Promise<{
    success: boolean;
    message: string;
    data?: ICreateTerminalResponse;
  }> => {
    try {
      const { data } = await api.post(URLS.terminalsRegisterBatch, payload);

      return {
        success: true,
        message: t("backoffice.terminalsServiceCreateSuccess"),
        data,
      };
    } catch (error: any) {
      return handleApiError(error, t("backoffice.terminalsServiceCreateError"));
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.TERMINALS, "register-batch"],
    mutationFn: (payload: ICreateTerminalPayload) => registerTerminalsBatch(payload),
  });
};

export const useGetTerminalDetails = (serialNumber: string) => {
  const { t } = useTranslation();

  const getTerminalDetails = async (): Promise<{
    success: boolean;
    message: string;
    data?: ITerminalDetails;
  }> => {
    try {
      const { data } = await api.get(URLS.terminalDetails(serialNumber));

      return {
        success: true,
        message: t("backoffice.terminalsServiceDetailsSuccess"),
        data,
      };
    } catch (error: any) {
      return handleApiError(error, t("backoffice.terminalsServiceDetailsError"));
    }
  };

  return useQuery({
    queryKey: [BACKOFFICE_QUERIES.TERMINAL_DETAILS, serialNumber],
    queryFn: () => getTerminalDetails(),
    enabled: !!serialNumber,
    staleTime: 30_000,
  });
};

export const useResolveTerminalNames = () => {
  const resolveTerminalNames = async (
    payload: IResolveNamesPayload,
  ): Promise<{
    success: boolean;
    message: string;
    data?: IResolveNamesResponse;
  }> => {
    try {
      const { data } = await api.post(URLS.resolveNames, payload);

      return { success: true, message: '', data };
    } catch (error: any) {
      return handleApiError(error, 'Erro ao resolver nomes dos terminais');
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.TERMINALS, 'resolve-names'],
    mutationFn: (payload: IResolveNamesPayload) => resolveTerminalNames(payload),
  });
};

export const useUnconfigureTerminals = () => {
  const { t } = useTranslation();

  const unconfigureTerminals = async (
    payload: IUnconfigureTerminalPayload,
  ): Promise<{
    success: boolean;
    message: string;
    data?: IConfigureTerminalResponse;
  }> => {
    try {
      const { data } = await api.post(URLS.terminalsUnconfigure, payload);

      return {
        success: true,
        message: t('backoffice.terminalsServiceUnconfigureSuccess'),
        data,
      };
    } catch (error: any) {
      return handleApiError(error, t('backoffice.terminalsServiceUnconfigureError'));
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.TERMINALS, 'unconfigure'],
    mutationFn: (payload: IUnconfigureTerminalPayload) => unconfigureTerminals(payload),
  });
};

export const useConfigureTerminals = () => {
  const { t } = useTranslation();

  const configureTerminals = async (
    payload: IConfigureTerminalPayload,
  ): Promise<{
    success: boolean;
    message: string;
    data?: IConfigureTerminalResponse;
  }> => {
    try {
      const { data } = await api.post(URLS.terminalsConfigure, payload);

      return {
        success: true,
        message: t('backoffice.terminalsServiceConfigureSuccess'),
        data,
      };
    } catch (error: any) {
      return handleApiError(error, t('backoffice.terminalsServiceConfigureError'));
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.TERMINALS, 'configure'],
    mutationFn: (payload: IConfigureTerminalPayload) => configureTerminals(payload),
  });
};

export const useProcessTerminalsCsvBatch = () => {
  const { t } = useTranslation();

  const processTerminalsCsvBatch = async ({
    file,
    processingType,
  }: ICsvBatchPayload): Promise<{
    success: boolean;
    message: string;
    data?: IConfigureTerminalResponse;
  }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post(
        URLS.terminalsCsv(processingType),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      return {
        success: true,
        message: t('backoffice.terminalsServiceCsvBatchSuccess'),
        data,
      };
    } catch (error: any) {
      return handleApiError(error, t('backoffice.terminalsServiceCsvBatchError'));
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.TERMINALS, 'csv-batch'],
    mutationFn: (payload: ICsvBatchPayload) => processTerminalsCsvBatch(payload),
  });
};

export const useConfigureSupport = () => {
  const { t } = useTranslation();

  const configureSupport = async ({
    serialNumber,
    success,
    failureDescription,
  }: {
    serialNumber: string;
    success: boolean;
    failureDescription: string | null;
  }): Promise<{
    success: boolean;
    message: string;
  }> => {
    try {
      await api.post(URLS.configureSupport, {
        serialNumber,
        success,
        failureDescription,
      });

      return {
        success: true,
        message: success
          ? t("backoffice.terminalsServiceApproveSuccess")
          : t("backoffice.terminalsServiceRejectSuccess"),
      };
    } catch (error: any) {
      return handleApiError(
        error,
        success
          ? t("backoffice.terminalsServiceApproveError")
          : t("backoffice.terminalsServiceRejectError"),
      );
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.TERMINALS, "configure-support"],
    mutationFn: (payload: {
      serialNumber: string;
      success: boolean;
      failureDescription: string | null;
    }) => configureSupport(payload),
  });
};
